import React, { useState, useEffect } from 'react';
import axios from '../api/axios';

const UrgentTasksSection = () => {
  const [urgentTasks, setUrgentTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [availableSemesters, setAvailableSemesters] = useState([]);
  const [selectedSemester, setSelectedSemester] = useState('');
  const [filters, setFilters] = useState({
    team: 'ALL',
    daysFrom: 7,
    daysTo: -14
  });

  useEffect(() => {
    fetchAvailableSemesters();
  }, []);

  useEffect(() => {
    if (selectedSemester) {
      fetchUrgentTasksWithFilters();
    }
  }, [selectedSemester, filters]);

  const fetchAvailableSemesters = async () => {
    try {
      // 모든 학기의 action plans를 가져와서 unique한 semester+branch 리스트 생성
      const response = await axios.get('/api/action-plans?semester=all');
      const semesters = [...new Set(response.data.map(task => `${task.semester} (${task.branch})`))].sort();
      setAvailableSemesters(semesters);
    } catch (error) {
      setAvailableSemesters([]);
    }
  };

  const fetchUrgentTasksWithFilters = async () => {
    if (!selectedSemester) return;
    
    try {
      setLoading(true);
      // "2024-Fall (NCCU)" 형식에서 학기명과 브랜치 추출
      const [semester, branch] = selectedSemester.includes('(') 
        ? selectedSemester.split(' (')
        : [selectedSemester, ''];
      const cleanBranch = branch.replace(')', '');
      
      const response = await axios.get(`/api/action-plans?semester=${semester}`);
      // 브랜치별로 필터링
      let filteredTasks = response.data.filter(task => task.branch === cleanBranch);
      
      // 팀 필터링
      if (filters.team !== 'ALL') {
        filteredTasks = filteredTasks.filter(task => task.team === filters.team);
      }
      
      // D-day 필터링 (올바른 로직)
      const today = new Date();
      today.setHours(0, 0, 0, 0); // 시간을 00:00:00으로 설정
      
      // From: D+30 (30일 후) - 미래의 시작점
      const fromDate = new Date(today);
      fromDate.setDate(today.getDate() + filters.daysFrom);
      
      // To: D-0 (오늘) - 과거의 끝점  
      const toDate = new Date(today);
      toDate.setDate(today.getDate() + filters.daysTo);
      
      filteredTasks = filteredTasks.filter(task => {
        const taskDate = new Date(task.deadline);
        taskDate.setHours(0, 0, 0, 0); // 시간을 00:00:00으로 설정
        
        // D+30부터 D-0까지: toDate <= taskDate <= fromDate
        // 즉, 오늘부터 30일 후까지의 범위
        return taskDate >= toDate && taskDate <= fromDate;
      });
      
      setUrgentTasks(filteredTasks);
    } catch (error) {
      setUrgentTasks([]);
    } finally {
      setLoading(false);
    }
  };

  const getTeamColor = (team) => {
    switch (team) {
      case 'GA':
        return 'bg-green-100 text-green-800';
      case 'PR':
        return 'bg-purple-100 text-purple-800';
      case 'EP':
        return 'bg-blue-100 text-blue-800';
      case 'Collective':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const calculateDDay = (deadline) => {
    const today = new Date();
    const deadlineDate = new Date(deadline);
    const diffTime = deadlineDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays > 0) {
      return `D-${diffDays}`;
    } else if (diffDays === 0) {
      return 'D-Day';
    } else {
      return `D+${Math.abs(diffDays)}`;
    }
  };

  const getDDayColor = (deadline) => {
    const today = new Date();
    const deadlineDate = new Date(deadline);
    const diffTime = deadlineDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return 'text-red-600';
    if (diffDays === 0) return 'text-red-800 font-bold';
    if (diffDays <= 3) return 'text-orange-600';
    if (diffDays <= 7) return 'text-yellow-600';
    return 'text-green-600';
  };

  // 전체 진행률 계산
  const totalTasks = urgentTasks.length;
  const completedTasks = urgentTasks.filter(task => task.status === 'DONE').length;
  const progressPercentage = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

  // 팀별 진행률 계산
  const teamStats = {};
  urgentTasks.forEach(task => {
    if (!teamStats[task.team]) {
      teamStats[task.team] = { total: 0, completed: 0 };
    }
    teamStats[task.team].total++;
    if (task.status === 'DONE') {
      teamStats[task.team].completed++;
    }
  });

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-xl font-bold text-gray-900 mb-4">Urgent Tasks</h2>
      
      {/* Available Action Plans */}
      {availableSemesters.length > 0 && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h3 className="text-lg font-medium text-blue-900 mb-3">Available Action Plans</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {availableSemesters.map((semesterKey) => (
              <button
                key={semesterKey}
                onClick={() => setSelectedSemester(selectedSemester === semesterKey ? '' : semesterKey)}
                className={`p-3 rounded-lg border-2 text-left transition-colors ${
                  selectedSemester === semesterKey
                    ? 'border-blue-500 bg-blue-100 text-blue-900'
                    : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50'
                }`}
              >
                <div className="font-medium">{semesterKey}</div>
                <div className="text-sm text-gray-600">Click to view tasks</div>
              </button>
            ))}
          </div>
          {selectedSemester && (
            <div className="mt-4 flex justify-between items-center">
              <div>
                <span className="text-sm text-blue-700">Selected: {selectedSemester}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Progress and Filters */}
      {selectedSemester && (
        <>
          {/* 전체 진행률 - 게임 스타일 */}
          <div className="mb-6 p-6 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border-2 border-blue-200">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xl font-bold text-gray-900 flex items-center">
                🎯 Mission Progress
                <span className="ml-2 text-sm font-normal text-gray-600">Complete tasks to level up!</span>
              </h3>
              <div className="text-right">
                <span className="text-lg font-bold text-blue-600">{completedTasks}/{totalTasks}</span>
                <span className="text-sm text-gray-600 ml-1">Done</span>
              </div>
            </div>
            <div className="relative">
              <div className="w-full bg-gray-300 rounded-full h-6 mb-3 shadow-inner">
                <div 
                  className="bg-gradient-to-r from-green-400 to-blue-500 h-6 rounded-full transition-all duration-500 ease-out shadow-lg"
                  style={{ width: `${progressPercentage}%` }}
                ></div>
              </div>
              <div className="text-center">
                <span className="text-3xl font-bold bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">
                  {progressPercentage}%
                </span>
                <p className="text-sm text-gray-600 mt-1">
                  {progressPercentage === 100 ? "🎉 Mission Complete! All tasks done!" : 
                   progressPercentage >= 75 ? "🔥 Almost there! Keep going!" :
                   progressPercentage >= 50 ? "💪 Halfway through! Great progress!" :
                   progressPercentage >= 25 ? "🚀 Good start! Keep it up!" :
                   "🎯 Let's get started! Complete tasks to boost your progress!"}
                </p>
              </div>
            </div>
          </div>

          {/* 팀별 진행률 - 게임 스타일 */}
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
              🏆 Team Leaderboard
              <span className="ml-2 text-sm font-normal text-gray-600">Which team will win?</span>
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {Object.entries(teamStats).map(([team, stats]) => {
                const teamProgress = stats.total > 0 ? Math.round((stats.completed / stats.total) * 100) : 0;
                const teamEmoji = team === 'GA' ? '💚' : team === 'PR' ? '💜' : team === 'EP' ? '💙' : '🤍';
                const teamGradient = team === 'GA' ? 'from-green-400 to-green-600' : 
                                   team === 'PR' ? 'from-purple-400 to-purple-600' : 
                                   team === 'EP' ? 'from-blue-400 to-blue-600' : 'from-gray-400 to-gray-600';
                
                return (
                  <div key={team} className="bg-white border-2 border-gray-200 rounded-lg p-4 hover:shadow-lg transition-shadow">
                    <div className="flex items-center justify-between mb-3">
                      <span className={`px-3 py-1 text-sm font-bold rounded-full ${getTeamColor(team)} flex items-center`}>
                        {teamEmoji} {team}
                      </span>
                      <span className="text-sm font-bold text-gray-700">{stats.completed}/{stats.total}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3 mb-2 shadow-inner">
                      <div 
                        className={`bg-gradient-to-r ${teamGradient} h-3 rounded-full transition-all duration-500 ease-out shadow-sm`}
                        style={{ width: `${teamProgress}%` }}
                      ></div>
                    </div>
                    <div className="text-center">
                      <span className="text-lg font-bold text-gray-800">{teamProgress}%</span>
                      <p className="text-xs text-gray-500 mt-1">
                        {teamProgress === 100 ? "🏆 Perfect!" : 
                         teamProgress >= 75 ? "🔥 Excellent!" :
                         teamProgress >= 50 ? "💪 Good job!" :
                         teamProgress >= 25 ? "🚀 Keep going!" :
                         "🎯 Let's start!"}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* 필터 */}
          <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Team</label>
              <select
                value={filters.team}
                onChange={(e) => setFilters(prev => ({ ...prev, team: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="ALL">All Teams</option>
                <option value="GA">GA</option>
                <option value="PR">PR</option>
                <option value="EP">EP</option>
                <option value="Collective">Collective</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">From (D-day)</label>
              <select
                value={filters.daysFrom}
                onChange={(e) => setFilters(prev => ({ ...prev, daysFrom: parseInt(e.target.value) }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value={90}>D+90</option>
                <option value={60}>D+60</option>
                <option value={30}>D+30</option>
                <option value={21}>D+21</option>
                <option value={14}>D+14</option>
                <option value={7}>D+7</option>
                <option value={0}>D-0 (Today)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">To (D-day)</label>
              <select
                value={filters.daysTo}
                onChange={(e) => setFilters(prev => ({ ...prev, daysTo: parseInt(e.target.value) }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value={0}>D-0 (Today)</option>
                <option value={-7}>D-7</option>
                <option value={-14}>D-14</option>
                <option value={-21}>D-21</option>
                <option value={-30}>D-30</option>
                <option value={-60}>D-60</option>
                <option value={-90}>D-90</option>
              </select>
            </div>
          </div>

          {/* Tasks List - 팀별 컬럼 레이아웃 */}
          {loading ? (
            <div className="flex justify-center items-center h-32">
              <p className="text-gray-500">Loading...</p>
            </div>
          ) : urgentTasks.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No tasks found for the selected criteria.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* GA 팀 */}
              <div className="bg-white border rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                  <span className="w-3 h-3 bg-green-500 rounded-full mr-2"></span>
                  GA Team
                </h3>
                <div className="space-y-2">
                  {urgentTasks.filter(task => task.team === 'GA').map((task) => (
                    <div key={task.id} className="p-2 bg-gray-50 rounded border-l-4 border-green-500">
                      <p className="font-medium text-sm text-gray-900">{task.taskName}</p>
                      <div className="flex items-center justify-between mt-1">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          task.status === 'TODO' ? 'bg-red-100 text-red-800' :
                          task.status === 'IN_PROGRESS' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {task.status === 'TODO' ? 'To Do' :
                           task.status === 'IN_PROGRESS' ? 'In Progress' : 'Done'}
                        </span>
                        <span className={`font-bold text-xs ${getDDayColor(task.deadline)}`}>
                          {calculateDDay(task.deadline)}
                        </span>
                      </div>
                    </div>
                  ))}
                  {urgentTasks.filter(task => task.team === 'GA').length === 0 && (
                    <p className="text-gray-400 text-sm text-center py-4">No GA tasks</p>
                  )}
                </div>
              </div>

              {/* PR 팀 */}
              <div className="bg-white border rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                  <span className="w-3 h-3 bg-purple-500 rounded-full mr-2"></span>
                  PR Team
                </h3>
                <div className="space-y-2">
                  {urgentTasks.filter(task => task.team === 'PR').map((task) => (
                    <div key={task.id} className="p-2 bg-gray-50 rounded border-l-4 border-purple-500">
                      <p className="font-medium text-sm text-gray-900">{task.taskName}</p>
                      <div className="flex items-center justify-between mt-1">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          task.status === 'TODO' ? 'bg-red-100 text-red-800' :
                          task.status === 'IN_PROGRESS' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {task.status === 'TODO' ? 'To Do' :
                           task.status === 'IN_PROGRESS' ? 'In Progress' : 'Done'}
                        </span>
                        <span className={`font-bold text-xs ${getDDayColor(task.deadline)}`}>
                          {calculateDDay(task.deadline)}
                        </span>
                      </div>
                    </div>
                  ))}
                  {urgentTasks.filter(task => task.team === 'PR').length === 0 && (
                    <p className="text-gray-400 text-sm text-center py-4">No PR tasks</p>
                  )}
                </div>
              </div>

              {/* EP 팀 */}
              <div className="bg-white border rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                  <span className="w-3 h-3 bg-blue-500 rounded-full mr-2"></span>
                  EP Team
                </h3>
                <div className="space-y-2">
                  {urgentTasks.filter(task => task.team === 'EP').map((task) => (
                    <div key={task.id} className="p-2 bg-gray-50 rounded border-l-4 border-blue-500">
                      <p className="font-medium text-sm text-gray-900">{task.taskName}</p>
                      <div className="flex items-center justify-between mt-1">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          task.status === 'TODO' ? 'bg-red-100 text-red-800' :
                          task.status === 'IN_PROGRESS' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {task.status === 'TODO' ? 'To Do' :
                           task.status === 'IN_PROGRESS' ? 'In Progress' : 'Done'}
                        </span>
                        <span className={`font-bold text-xs ${getDDayColor(task.deadline)}`}>
                          {calculateDDay(task.deadline)}
                        </span>
                      </div>
                    </div>
                  ))}
                  {urgentTasks.filter(task => task.team === 'EP').length === 0 && (
                    <p className="text-gray-400 text-sm text-center py-4">No EP tasks</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {availableSemesters.length === 0 && (
        <p className="text-gray-500 text-center py-8">No action plans available. Create one in Action Plan Management.</p>
      )}
    </div>
  );
};

export default UrgentTasksSection;